import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { BDM_SOURCE, BDM_SOURCE_LABEL } from "../../utils/franchiseAdminModel";
import { visibleFacilityColumns } from "./FacilityGrid";
import EditFacilityDetailsDialog from "./dialogs/EditFacilityDetailsDialog";
import EditFacilityOwnershipDialog from "./dialogs/EditFacilityOwnershipDialog";
import SiteManagementDialog from "./dialogs/SiteManagementDialog";

// One facility (datamart.dim_facility) as a READ-ONLY row. No inputs, no checkboxes, no selects
// (design §3 rule 4) -- every mutation goes through a focused dialog, added from slice A3.

// Matches the shipped Slice 4b chip vocabulary exactly so the redesigned grid and the classic
// row read identically during side-by-side verification. The authoritative states are filled;
// the fallback states are muted outlines so Direct / Inactive stay visually primary.
function BdmSourceChip(props) {
  const source = props.source;

  if (source === BDM_SOURCE.DIRECT_INACTIVE) {
    return <Chip label={BDM_SOURCE_LABEL[source]} color="warning" size="small" />;
  }
  if (source === BDM_SOURCE.DIRECT) {
    return <Chip label={BDM_SOURCE_LABEL[source]} color="primary" size="small" />;
  }
  if (source === BDM_SOURCE.NONE) {
    return <Chip label={BDM_SOURCE_LABEL[source]} size="small" variant="outlined" />;
  }
  return (
    <Chip
      label={BDM_SOURCE_LABEL[BDM_SOURCE.INHERITED]}
      size="small"
      variant="outlined"
      sx={{ color: "text.secondary", borderColor: "divider" }}
    />
  );
}

// Tri-state PaceSetter / Next Level Leader. Plain text rather than a chip: this grid is dense and
// two more chips per row would compete with the BDM source chip for attention. `null` (not
// configured) is deliberately rendered differently from an explicit `No`.
function TriStateCell(props) {
  const value = props.value;

  if (value == null) {
    return (
      <Typography variant="body2" color="text.disabled" aria-label="not configured">
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color={value === 1 ? "success.main" : "text.secondary"} fontWeight={value === 1 ? 600 : 400}>
      {value === 1 ? "Yes" : "No"}
    </Typography>
  );
}

export default function FacilityRow(props) {
  const facility = props.facility;
  const showActions = Boolean(props.showActions);
  const bdmOptions = props.bdmOptions;
  const onSaveOwnership = props.onSaveOwnership;
  const onSaveDetails = props.onSaveDetails;
  const onSaveSite = props.onSaveSite;
  const onDeleteSite = props.onDeleteSite;
  const onSaved = props.onSaved;
  // Supplied only by the flat Facilities lens (A7), where rows are no longer grouped under a
  // franchise band and therefore have to carry their own franchise identity.
  const franchiseName = props.franchiseName;

  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sitesOpen, setSitesOpen] = useState(false);

  const columns = visibleFacilityColumns(showActions, franchiseName != null);
  const columnIds = columns.map((column) => column.id);

  const cells = [];

  if (columnIds.includes("franchise")) {
    cells.push(
      <TableCell key="franchise">
        <Typography variant="body2" color="text.secondary" noWrap>
          {franchiseName}
        </Typography>
      </TableCell>
    );
  }

  cells.push(
    <TableCell key="name">
      <Typography variant="body2" fontWeight={500}>
        {facility.name}
      </Typography>
    </TableCell>
  );

  cells.push(
    <TableCell key="status">
      {facility.isActive ? (
        <Chip label="Active" size="small" variant="outlined" color="success" />
      ) : (
        <Chip label="Inactive" size="small" variant="outlined" sx={{ color: "text.secondary", borderColor: "divider" }} />
      )}
    </TableCell>
  );

  // Multi-BDM slice 3, read-only. The cell keeps showing the PRIMARY owner and adds "+N" for the
  // rest, so shared ownership is visible at a glance without widening the column.
  const ownership = facility.ownership;
  const additionalOwners = ownership ? ownership.additionalCount : 0;

  // "Unlinked" is shown ONLY for an unresolved name at the FACILITY grain. The ordinary inherited
  // case is unresolved too -- the franchise default is free text for every franchise until the
  // cutover -- so flagging it here would mark ~170 of 211 rows and say nothing the "Inherited"
  // chip does not already say. A direct unresolved value, by contrast, is a genuine anomaly.
  const showUnlinked = Boolean(
    ownership && ownership.unresolvedNames.length > 0 && !ownership.isInherited
  );

  cells.push(
    <TableCell key="bdm">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
          {facility.effectiveBdmName || "—"}
        </Typography>
        {additionalOwners > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
            title={`Shared with ${additionalOwners} other ${additionalOwners === 1 ? "BDM" : "BDMs"}`}
          >
            {`+${additionalOwners}`}
          </Typography>
        )}
        <BdmSourceChip source={facility.bdmSource} />
        {showUnlinked && (
          <Chip
            label="Unlinked"
            size="small"
            variant="outlined"
            color="warning"
            title="Stored as text; not linked to a person in the BDM directory"
          />
        )}
      </Box>
    </TableCell>
  );

  cells.push(
    <TableCell key="pacesetter" align="center">
      <TriStateCell value={facility.pacesetter} />
    </TableCell>
  );

  cells.push(
    <TableCell key="nll" align="center">
      <TriStateCell value={facility.nextLevelLeader} />
    </TableCell>
  );

  // Neutral count summary. Having no Sites yet is a normal state, not an error, so it is never
  // styled as a warning. Detailed asset data (fields, space, indoor/outdoor) lives in the Manage
  // Sites dialog. The count already excludes the synthetic placeholder via realSites().
  cells.push(
    <TableCell key="sites" align="center">
      <Typography
        variant="body2"
        noWrap
        color={facility.siteCount === 0 ? "text.secondary" : "text.primary"}
      >
        {facility.siteCount === 0
          ? "No sites"
          : `${facility.siteCount} ${facility.siteCount === 1 ? "site" : "sites"}`}
      </Typography>
    </TableCell>
  );

  if (columnIds.includes("actions")) {
    // Visible text buttons per decision #13, not an overflow menu. Each action appears only once
    // its slice supplies a handler: Edit Ownership here (A3), Edit Details (A4), Manage Sites (A6).
    const actionButtons = [];

    if (onSaveDetails) {
      actionButtons.push(
        <Button
          key="details"
          size="small"
          onClick={() => setDetailsOpen(true)}
          sx={{ textTransform: "none", minWidth: 0, px: 0.75, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Edit Details
        </Button>
      );
    }

    if (onSaveOwnership) {
      actionButtons.push(
        <Button
          key="ownership"
          size="small"
          onClick={() => setOwnershipOpen(true)}
          sx={{ textTransform: "none", minWidth: 0, px: 0.75, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Edit Ownership
        </Button>
      );
    }

    if (onSaveSite) {
      actionButtons.push(
        <Button
          key="sites"
          size="small"
          onClick={() => setSitesOpen(true)}
          sx={{ textTransform: "none", minWidth: 0, px: 0.75, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Manage Sites
        </Button>
      );
    }

    cells.push(
      <TableCell key="actions" align="right">
        {/* nowrap: actions stay on one line so row height stays constant down the grid. */}
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "nowrap" }}>
          {actionButtons}
        </Box>
      </TableCell>
    );
  }

  return (
    <React.Fragment>
      <TableRow hover>{cells}</TableRow>
      {onSaveDetails && (
        <EditFacilityDetailsDialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          facility={facility}
          onSave={onSaveDetails}
          onSaved={onSaved}
        />
      )}
      {onSaveSite && (
        <SiteManagementDialog
          open={sitesOpen}
          onClose={() => setSitesOpen(false)}
          facility={facility}
          onSaveSite={onSaveSite}
          onDeleteSite={onDeleteSite}
          onSaved={onSaved}
        />
      )}
      {onSaveOwnership && (
        <EditFacilityOwnershipDialog
          open={ownershipOpen}
          onClose={() => setOwnershipOpen(false)}
          facility={facility}
          bdmOptions={bdmOptions}
          onSave={onSaveOwnership}
          onSaved={onSaved}
        />
      )}
    </React.Fragment>
  );
}
